import React from 'react';
import { NavLink } from 'react-router-dom';
import { usePartnerQueue } from '../hooks/usePartnerQueue';
import ConsoleShell from './ConsoleShell';

/**
 * The admin console shell (`ConsoleShell`) with the settings nav, wrapping the
 * /settings/* screens.
 *
 * `title`/`subtitle` render a navy-900 hero band at the top of the content
 * area — the same chrome every settings page now shares, one level up from
 * each page's own body. Omit `title` on a page that renders its own
 * equivalent header (the partner form builder's `fullBleed` page: its
 * `@kevlarcon/formkit` `FormBuilder` ships its own hero band, recolored to
 * this same navy/gold in sgokit.css, so a second one here would be a
 * doubled-up header). `actions` renders alongside the title (page-level
 * buttons like Partners' "Add partner"/"Bulk import") — use `sgk-btn--onnavy`
 * rather than `sgk-btn--ghost` there, since the hero sits on `--navy-900`.
 */
const AdminLayout: React.FC<{
  children: React.ReactNode;
  title?: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  fullBleed?: boolean;
  wide?: boolean;
}> = ({ children, ...shell }) => {
  const { queue } = usePartnerQueue();
  const pendingCount = queue?.length ?? 0;

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `sgk-admin__navlink${isActive ? ' is-active' : ''}`;

  const nav = (
    <>
      <NavLink to="/settings/donations" className={linkClass} data-testid="admin-nav-donations-link">
        Donations
      </NavLink>
      <NavLink to="/settings/states" className={linkClass} data-testid="admin-nav-states-link">
        States
      </NavLink>
      <NavLink to="/settings/partners" className={linkClass} data-testid="admin-nav-partners-link">
        Partners
        {pendingCount > 0 && (
          <span className="sgk-admin__navbadge" data-testid="admin-nav-partners-badge">
            {pendingCount}
          </span>
        )}
      </NavLink>
      <NavLink to="/settings/site" className={linkClass} data-testid="admin-nav-site-link">
        Site
      </NavLink>
    </>
  );

  return (
    <ConsoleShell nav={nav} testIdPrefix="admin" {...shell}>
      {children}
    </ConsoleShell>
  );
};

export default AdminLayout;
